import React, { useState, useCallback } from 'react';
import { Tag, seg } from '../tags/Tag';
import { View, ViewOff } from '@core/icons-react';
import type { SensitiveCellProps } from './types';

/**
 * SensitiveCell - Renders masked/sensitive data with reveal toggle
 * 
 * Features:
 * - Full or partial masking
 * - Reveal on click
 * - Configurable max length and masking
 */
export const SensitiveCell: React.FC<SensitiveCellProps> = ({ value, config, row }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  
  if (value === null || value === undefined) {
    return null;
  }
  
  const stringValue = String(value);
  
  // Handle masking
  const getMaskedValue = useCallback(() => {
    if (!config?.maskingType || config.maskingType === 'full') {
      // Full masking - all asterisks
      const length = config?.maskLength || stringValue.length;
      return '•'.repeat(Math.min(length, 20));
    } else if (config.maskingType === 'partial') {
      const visibleCount = config?.visibleChars || 8;
      if (stringValue.length <= visibleCount) {
        return stringValue;
      }
      const visibleChars = stringValue.slice(0, visibleCount);
      const maskedLength = config?.maskLength || Math.min(stringValue.length - visibleCount, 8);
      return `${visibleChars}${'•'.repeat(maskedLength)}`;
    }
    return stringValue;
  }, [stringValue, config]);
  
  // Handle truncation with ellipsis
  const getTruncatedValue = useCallback((text: string) => {
    const maxLength = config?.maxLength || 40;
    if (text.length > maxLength) {
      return `${text.slice(0, maxLength)}...`;
    }
    return text;
  }, [config?.maxLength]);
  
  const displayValue = isRevealed 
    ? getTruncatedValue(stringValue) 
    : getTruncatedValue(getMaskedValue());
  
  const handleClick = useCallback(() => {
    if (config?.onClick) {
      // Custom click handler (e.g., open modal)
      config.onClick(value, row);
    } else if (config?.allowReveal !== false) {
      // Default behavior: toggle reveal
      setIsRevealed(!isRevealed);
    }
  }, [config, value, row, isRevealed]);
  
  // Determine which icon to show
  const RevealIcon = isRevealed ? ViewOff : View;
  
  // Check if we should show as clickable
  const hasClickHandler = !!config?.onClick || config?.allowReveal !== false;
  
  return (
    <Tag
      segments={[seg.text(displayValue), seg.icon(RevealIcon)]}
      onClick={hasClickHandler ? handleClick : undefined}
      size="sm"
    />
  );
};

